import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, Loader2, LogOut, Send, FileBarChart, Ban, BellRing, Sparkle, Eye, Check, Plus, Download } from "lucide-react";

const TIER_TO_KEY = {
  "Essential Pulse": "essential_setup",
  "Concierge Plus": "plus_setup",
  "Grand Concierge Suite": "grand_setup",
};

const PRODUCT_TO_MONTHLY = {
  "Essential Pulse": "essential_monthly",
  "Concierge Plus": "plus_monthly",
  "Grand Concierge Suite": "grand_monthly",
};

const ONBOARDING_STEPS = [
  "Operations Audit Call",
  "GBP Connected",
  "Templates Set",
  "14-Day Pulse Launched",
  "Reporting Live",
];

const LEAD_BUSINESS_TYPES = [
  "Restaurant",
  "HVAC / Home Services",
  "Contractor",
  "Chiropractor / Medical",
  "Salon / Med Spa",
  "Retail / Grocery",
  "Automotive",
  "Other",
];

const LOW_RATINGS = ["No reviews yet", "Under 2 stars", "2–3 stars"];

const BUSINESS_MODEL_FILTERS = [
  "Hospitality & Accommodations",
  "Service-Based Business",
  "Experience & Community Business",
];

const SEQUENCE_LABELS = {
  recovery: ["Your time debt is compounding", "What 90 days could return to you"],
  elevation: ["From strong to iconic", "The 90-day elevation"],
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const safeCopy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const monthLabel = () =>
  new Date().toLocaleString("en-US", { month: "long", year: "numeric" });

export default function AdminLeads() {
  const { logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [subs, setSubs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterRating, setFilterRating] = useState("all");
  const [filterModel, setFilterModel] = useState("all");

  // invoice dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", lookup_key: "", lead_id: null });
  const [sending, setSending] = useState(false);
  const [resultUrl, setResultUrl] = useState("");
  const [emailSent, setEmailSent] = useState(null);

  // report dialog
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [reportSending, setReportSending] = useState(false);
  const [preview, setPreview] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [history, setHistory] = useState([]);

  // onboarding
  const [onboarding, setOnboarding] = useState([]);
  const [obOpen, setObOpen] = useState(false);
  const [obForm, setObForm] = useState({ name: "", email: "" });

  // analytics
  const [analytics, setAnalytics] = useState(null);
  const [followups, setFollowups] = useState(null);
  const [abandoned, setAbandoned] = useState(null);

  const loadOnboarding = () => api.get("/onboarding").then((r) => setOnboarding(r.data)).catch(() => {});
  const loadAnalytics = () => api.get("/analytics/summary").then((r) => setAnalytics(r.data)).catch(() => {});
  const loadFollowups = () => api.get("/followups").then((r) => setFollowups(r.data)).catch(() => {});
  const loadAbandoned = () => api.get("/audit-starts").then((r) => setAbandoned(r.data)).catch(() => {});

  const controlFollowups = async (email, action) => {
    try {
      const { data } = await api.post("/followups/control", { email, action });
      const label = action === "cancel" ? "cancelled" : action === "pause" ? "paused" : "resumed";
      toast.success(`Follow-ups ${label} (${data.updated} email${data.updated !== 1 ? "s" : ""}).`);
      loadFollowups();
    } catch {
      toast.error("Couldn't update the follow-ups.");
    }
  };

  const loadPayments = () => api.get("/payments").then((r) => setPayments(r.data)).catch(() => {});
  const loadSummary = () => api.get("/payments/summary").then((r) => setSummary(r.data)).catch(() => {});
  const loadSubs = () => api.get("/subscriptions").then((r) => setSubs(r.data)).catch(() => {});

  const cancelSub = async (s) => {
    if (!window.confirm(`Cancel ${s.product} for ${s.email}? This stops future billing.`)) return;
    try {
      await api.post(`/subscriptions/${s.id}/cancel`);
      toast.success("Subscription canceled.");
      loadSubs();
      loadSummary();
    } catch (e) {
      toast.error("Could not cancel. Please try again.");
    }
  };

  const remindSub = async (s) => {
    try {
      const { data } = await api.post(`/subscriptions/${s.id}/remind`);
      toast[data.email_sent ? "success" : "error"](
        data.email_sent ? `Reminder emailed to ${data.email}.` : "Couldn't deliver the reminder."
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reminder.");
    }
  };

  const winbackSub = async (s) => {
    const key = PRODUCT_TO_MONTHLY[s.product];
    if (!key) {
      toast.error("Couldn't match a plan for this client.");
      return;
    }
    try {
      const { data } = await api.post("/admin/winback", {
        name: s.email, email: s.email, lookup_key: key, origin_url: window.location.origin,
      });
      loadPayments();
      if (data.checkout_url) await safeCopy(data.checkout_url);
      toast.success(
        data.email_sent ? `Welcome-back offer emailed to ${s.email}.` : "Offer link created & copied to clipboard.",
        { description: "30% off their first month back." }
      );
    } catch (e) {
      toast.error("Could not create the win-back offer.");
    }
  };

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/payments/tiers").then((r) => setTiers(r.data)).catch(() => {});
    api.get("/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    loadPayments();
    loadSummary();
    loadOnboarding();
    loadAnalytics();
    loadFollowups();
    loadAbandoned();
  }, []);

  // ---- Onboarding ----
  const addOnboarding = async () => {
    if (!obForm.name.trim() || !obForm.email.trim()) {
      toast.error("Add a client name and email.");
      return;
    }
    const items = Object.fromEntries(ONBOARDING_STEPS.map((s) => [s, false]));
    try {
      await api.put("/onboarding", { client_name: obForm.name, client_email: obForm.email, items });
      setObForm({ name: "", email: "" });
      setObOpen(false);
      loadOnboarding();
      toast.success("Onboarding checklist created.");
    } catch (e) {
      toast.error("Could not create the checklist.");
    }
  };

  const toggleStep = async (record, step) => {
    const items = { ...record.items, [step]: !record.items?.[step] };
    setOnboarding((list) => list.map((o) => (o.client_email === record.client_email ? { ...o, items } : o)));
    try {
      await api.put("/onboarding", { client_name: record.client_name, client_email: record.client_email, items });
    } catch (e) {
      toast.error("Could not save. Reloading.");
      loadOnboarding();
    }
  };

  // ---- Invoice ----
  const openInvoice = (lead) => {
    setResultUrl("");
    setEmailSent(null);
    setForm({
      name: lead?.name || "",
      email: lead?.email || "",
      lookup_key: (lead?.tier && TIER_TO_KEY[lead.tier]) || "",
      lead_id: lead?.id || null,
    });
    setDialogOpen(true);
  };

  const submitInvoice = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.lookup_key) {
      toast.error("Add a name, email, and choose a pricing option.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/invoice", {
        name: form.name, email: form.email, lookup_key: form.lookup_key,
        origin_url: window.location.origin, lead_id: form.lead_id,
      });
      setResultUrl(data.checkout_url);
      setEmailSent(data.email_sent);
      loadPayments();
      loadSummary();
      toast.success(
        data.email_sent
          ? `Payment link emailed to ${form.email}.`
          : "Link created — email couldn't be delivered to that address, copy it below."
      );
    } catch (e) {
      toast.error("Could not create the invoice. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    const ok = await safeCopy(resultUrl);
    toast[ok ? "success" : "error"](ok ? "Link copied." : "Copy failed — select the link manually.");
  };

  // ---- Report ----
  const openReport = async (preset) => {
    setPreview("");
    const base = {
      client_name: preset?.name || "",
      client_email: preset?.email || "",
      business_name: preset?.business || preset?.name || "",
      month: monthLabel(),
      rating: "", new_reviews: "", total_reviews: "", ranking_note: "", notes: "",
    };
    setReport(base);
    setReportOpen(true);
    setHistory([]);
    const email = preset?.email;
    if (email) {
      api.get(`/reports?email=${encodeURIComponent(email)}`).then((r) => setHistory(r.data)).catch(() => {});
      try {
        const { data } = await api.get(`/reports/latest?email=${encodeURIComponent(email)}`);
        if (data && data.client_email) {
          setReport((r) => ({
            ...r,
            client_name: data.client_name || r.client_name,
            business_name: data.business_name || r.business_name,
            rating: data.rating || "", new_reviews: data.new_reviews || "",
            total_reviews: data.total_reviews || "", ranking_note: data.ranking_note || "",
            notes: data.notes || "",
          }));
          toast("Pre-filled from last month's report — update what changed.");
        }
      } catch (e) { /* no prior report */ }
    }
  };

  const previewReport = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/report/preview", report);
      setPreview(data.html);
    } catch (e) {
      toast.error("Couldn't build the preview.");
    } finally {
      setPreviewing(false);
    }
  };

  const submitReport = async () => {
    if (!report.client_name.trim() || !report.client_email.trim() || !report.business_name.trim()) {
      toast.error("Add client name, email, and business name.");
      return;
    }
    setReportSending(true);
    try {
      const { data } = await api.post("/admin/report", report);
      toast[data.email_sent ? "success" : "error"](
        data.email_sent ? `Report emailed to ${report.client_email}.` : "Couldn't deliver to that email."
      );
      if (data.email_sent) setReportOpen(false);
    } catch (e) {
      toast.error("Could not send the report.");
    } finally {
      setReportSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-border py-2.5 text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-[var(--gold)] transition-colors";

  const StatCard = ({ label, value, testid, accent }) => (
    <div data-testid={testid} className="border border-border p-6 bg-background">
      <p className="overline">{label}</p>
      <p className={`mt-3 serif text-3xl lg:text-4xl ${accent ? "text-gold" : "text-foreground"}`}>{value}</p>
    </div>
  );

  const nowSec = Date.now() / 1000;
  const daysSince = (ts) => (ts ? Math.floor((nowSec - ts) / 86400) : 0);
  const atRisk = subs.filter(
    (s) => s.status === "past_due" || (!s.is_active && s.canceled_at && daysSince(s.canceled_at) <= 365)
  );

  const filteredLeads = leads.filter((l) => {
    if (filterType !== "all" && l.business_type !== filterType) return false;
    if (filterRating === "low" && !LOW_RATINGS.includes(l.google_rating || "")) return false;
    if (filterModel !== "all" && (l.business_model || "") !== filterModel) return false;
    return true;
  });

  const hotLeads = leads.filter((l) => {
    const recent = Date.now() - new Date(l.created_at).getTime() < 7 * 86400 * 1000;
    return recent && LOW_RATINGS.includes(l.google_rating || "");
  });

  const exportCsv = () => {
    const cols = ["created_at", "name", "business_name", "email", "phone", "city", "state", "business_type", "business_model", "audit_score", "audit_band", "google_rating", "tier", "message"];
    const header = ["Date", "Name", "Business", "Email", "Phone", "City", "State", "Type", "Business Model", "Audit Score", "Audit Band", "Rating", "Tier", "Message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filteredLeads.map((l) =>
      cols.map((c) => esc(c === "created_at" ? new Date(l[c]).toLocaleString() : l[c])).join(",")
    );
    const csv = [header.join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `beam-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredLeads.length} lead${filteredLeads.length !== 1 ? "s" : ""}.`);
  };

  return (
    <div className="min-h-screen bg-background text-foreground py-16">
      <div className="container-luxe">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="overline">Beam Concierge</p>
            <h1 className="mt-3 serif text-4xl tracking-tight">Concierge Desk</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="monthly-report-btn" onClick={() => openReport(null)}
              className="inline-flex items-center gap-2 border border-[var(--gold)] px-5 py-3 text-sm text-foreground hover:bg-[var(--gold)]/10 transition-colors">
              <FileBarChart size={15} /> Monthly Report
            </button>
            <button data-testid="new-invoice-btn" onClick={() => openInvoice(null)}
              className="inline-flex items-center gap-2 bg-secondary px-5 py-3 text-sm text-secondary-foreground hover:bg-[var(--gold)] transition-colors">
              <Send size={15} /> New Invoice
            </button>
            <button data-testid="logout-btn" onClick={logout}
              className="inline-flex items-center gap-2 border border-border px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:border-[var(--gold)] transition-colors">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        {/* Revenue Snapshot */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="revenue-snapshot">
          <StatCard testid="stat-collected" label="Total Collected" value={summary ? money(summary.total_collected) : "—"} accent />
          <StatCard testid="stat-mrr" label="Monthly Recurring" value={summary ? money(summary.mrr) : "—"} accent />
          <StatCard testid="stat-active" label="Active Clients" value={summary ? summary.active_subs : "—"} />
          <StatCard testid="stat-churned" label="Churned" value={summary ? summary.churned_subs : "—"} />
        </div>

        {/* Hot Leads — priority outreach */}
        {hotLeads.length > 0 && (
          <div data-testid="hot-leads-banner" className="mt-10 border border-[var(--gold)] bg-[var(--gold)]/[0.09]">
            <div className="px-6 py-4 flex items-center justify-between border-b border-[var(--gold)]/40">
              <p className="overline text-[var(--gold)] flex items-center gap-2">
                <Sparkle size={14} /> Priority Outreach · {hotLeads.length} hot lead{hotLeads.length > 1 ? "s" : ""}
              </p>
              <p className="text-xs text-muted-foreground">New leads with a low or missing Google rating — reach out first</p>
            </div>
            <div className="divide-y divide-border">
              {hotLeads.map((l) => (
                <div key={l.id} data-testid={`hot-lead-${l.id}`} className="px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 text-sm flex-wrap">
                    <span className="h-2 w-2 rounded-full bg-[var(--gold)]" />
                    <span className="font-medium">{l.name}</span>
                    <span className="text-muted-foreground">{l.business_name || l.business_type || "—"}</span>
                    <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 uppercase tracking-wider">{l.google_rating}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs hover:border-[var(--gold)] transition-colors">Email</a>
                    <button onClick={() => openInvoice(l)} className="inline-flex items-center gap-1.5 bg-[var(--gold)] px-3 py-1.5 text-xs text-[var(--charcoal)] hover:bg-[var(--gold-light)] transition-colors">
                      <Send size={13} /> Invoice
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* At-Risk Clients */}
        {atRisk.length > 0 && (
          <div data-testid="at-risk-banner" className="mt-10 border border-[var(--gold)]/50 bg-[var(--gold)]/[0.06]">
            <div className="px-6 py-4 flex items-center justify-between border-b border-[var(--gold)]/30">
              <p className="overline text-[var(--gold)]">At-Risk Clients · {atRisk.length}</p>
              <p className="text-xs text-muted-foreground">Win them back before they fully lapse</p>
            </div>
            <div className="divide-y divide-border">
              {atRisk.map((s) => (
                <div key={s.id} data-testid={`at-risk-${s.id}`} className="px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 text-sm flex-wrap">
                    <span className={`h-2 w-2 rounded-full ${s.status === "past_due" ? "bg-amber-500" : "bg-red-500"}`} />
                    <span className="font-medium">{s.email}</span>
                    <span className="text-muted-foreground">{s.product}</span>
                    <span className="uppercase text-xs tracking-wider text-muted-foreground">{s.status}</span>
                    {!s.is_active && daysSince(s.canceled_at) >= 30 && (
                      <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 uppercase tracking-wider" data-testid={`going-cold-${s.id}`}>
                        Going cold · {daysSince(s.canceled_at)}d
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {s.status === "past_due" && (
                      <button onClick={() => remindSub(s)} className="inline-flex items-center gap-1.5 border border-[var(--gold)] px-3 py-1.5 text-xs text-[var(--gold)] hover:bg-[var(--gold)]/10 transition-colors">
                        <BellRing size={13} /> Remind
                      </button>
                    )}
                    {!s.is_active && (
                      <button data-testid={`winback-banner-${s.id}`} onClick={() => winbackSub(s)} className="inline-flex items-center gap-1.5 bg-[var(--gold)] px-3 py-1.5 text-xs text-[var(--charcoal)] hover:bg-[var(--gold-light)] transition-colors">
                        <Sparkle size={13} /> Welcome Back · 30% off
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="inbox" className="mt-12">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="inbox" data-testid="tab-inbox">Inbox ({leads.length})</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments ({payments.length})</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">Subscriptions ({subs.length})</TabsTrigger>
            <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding ({onboarding.length})</TabsTrigger>
            <TabsTrigger value="followups" data-testid="tab-followups">
              Follow-Ups{followups ? ` (${followups.filter((f) => !f.sent && !f.cancelled).length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="abandoned" data-testid="tab-abandoned">
              Abandoned{abandoned ? ` (${abandoned.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Inbox */}
          <TabsContent value="inbox" className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="overline">Filter</span>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger data-testid="filter-business-type" className="w-[200px]">
                    <SelectValue placeholder="Business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All business types</SelectItem>
                    {LEAD_BUSINESS_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterRating} onValueChange={setFilterRating}>
                  <SelectTrigger data-testid="filter-rating" className="w-[190px]">
                    <SelectValue placeholder="Rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ratings</SelectItem>
                    <SelectItem value="low">Low / at-risk only</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterModel} onValueChange={setFilterModel}>
                  <SelectTrigger data-testid="filter-business-model" className="w-[240px]">
                    <SelectValue placeholder="Business model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All business models</SelectItem>
                    {BUSINESS_MODEL_FILTERS.map((m) => (
                      <SelectItem key={m} value={m} data-testid={`filter-model-option-${m}`}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(filterType !== "all" || filterRating !== "all" || filterModel !== "all") && (
                  <button data-testid="clear-filters" onClick={() => { setFilterType("all"); setFilterRating("all"); setFilterModel("all"); }}
                    className="text-sm text-muted-foreground hover:text-foreground underline">
                    Clear ({filteredLeads.length} of {leads.length})
                  </button>
                )}
              </div>
              <button data-testid="export-leads" onClick={exportCsv} disabled={filteredLeads.length === 0}
                className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-sm hover:border-[var(--gold)] transition-colors disabled:opacity-50">
                <Download size={14} /> Export CSV
              </button>
            </div>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead className="bg-[hsl(35_22%_94%)] text-left">
                  <tr className="[&>th]:px-5 [&>th]:py-3 [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-xs [&>th]:text-muted-foreground">
                    <th>Date</th><th>Name / Business</th><th>Contact</th><th>Type</th><th>Rating</th><th>Tier</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((l) => {
                    const low = LOW_RATINGS.includes(l.google_rating || "");
                    return (
                      <tr key={l.id} className="border-t border-border [&>td]:px-5 [&>td]:py-4 align-top">
                        <td className="whitespace-nowrap text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                        <td className="whitespace-nowrap">
                          <div>{l.name}</div>
                          <div className="text-xs text-muted-foreground">{l.business_name || "—"}</div>
                        </td>
                        <td className="whitespace-nowrap">
                          <div>{l.email}</div>
                          <div className="text-xs text-muted-foreground">{[l.phone, [l.city, l.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "—"}</div>
                        </td>
                        <td className="whitespace-nowrap">
                          <div>{l.business_type || "—"}</div>
                          {l.business_model && (
                            <div data-testid={`business-model-${l.id}`} className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                              {l.business_model}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap">
                          {l.google_rating ? (
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs ${low ? "bg-red-100 text-red-700" : "bg-[hsl(35_22%_92%)] text-muted-foreground"}`}>
                              {l.google_rating}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="whitespace-nowrap">
                          <div>{l.tier || "—"}</div>
                          {l.source === "advisory_audit" && (
                            <div data-testid={`audit-chip-${l.id}`} className="mt-1 inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-[var(--gold)]/15 text-[var(--gold)]">
                              Audit · {l.audit_score}/21
                            </div>
                          )}
                        </td>
                        <td>
                          <button data-testid={`invoice-btn-${l.id}`} onClick={() => openInvoice(l)}
                            className="inline-flex items-center gap-1.5 bg-secondary px-3 py-1.5 text-xs text-secondary-foreground hover:bg-[var(--gold)] transition-colors whitespace-nowrap">
                            <Send size={13} /> Invoice this lead
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && filteredLeads.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">
                      {leads.length === 0 ? "No leads yet." : "No leads match these filters."}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Payments */}
          <TabsContent value="payments" className="mt-6">
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-payments-table">
                <thead className="bg-[hsl(35_22%_94%)] text-left">
                  <tr className="[&>th]:px-5 [&>th]:py-3 [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-xs [&>th]:text-muted-foreground">
                    <th>Date</th><th>Client</th><th>Item</th><th>Amount</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const paid = p.payment_status === "paid";
                    const failed = ["failed", "expired"].includes(p.payment_status);
                    return (
                      <tr key={p.session_id} className="border-t border-border [&>td]:px-5 [&>td]:py-4 align-top">
                        <td className="whitespace-nowrap text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="whitespace-nowrap"><div>{p.client_name}</div><div className="text-xs text-muted-foreground">{p.client_email}</div></td>
                        <td className="whitespace-nowrap">{p.label}</td>
                        <td className="whitespace-nowrap">${Number(p.amount).toLocaleString()} {String(p.currency).toUpperCase()}</td>
                        <td>
                          <span data-testid={`payment-status-${p.session_id}`}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs uppercase tracking-wider ${paid ? "bg-[var(--gold)]/15 text-[var(--gold)]" : failed ? "bg-red-100 text-red-700" : "bg-[hsl(35_22%_92%)] text-muted-foreground"}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${paid ? "bg-[var(--gold)]" : failed ? "bg-red-500" : "bg-muted-foreground/60"}`} />
                            {p.payment_status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {payments.length === 0 && (<tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">No invoices yet.</td></tr>)}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Subscriptions */}
          <TabsContent value="subscriptions" className="mt-6">
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-subscriptions-table">
                <thead className="bg-[hsl(35_22%_94%)] text-left">
                  <tr className="[&>th]:px-5 [&>th]:py-3 [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-xs [&>th]:text-muted-foreground">
                    <th>Client</th><th>Plan</th><th>Monthly</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-t border-border [&>td]:px-5 [&>td]:py-4 align-top">
                      <td className="whitespace-nowrap">{s.email}</td>
                      <td className="whitespace-nowrap">{s.product}</td>
                      <td className="whitespace-nowrap">${Number(s.amount).toLocaleString()}/{s.interval}</td>
                      <td>
                        <span data-testid={`sub-status-${s.id}`}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs uppercase tracking-wider ${s.is_active ? "bg-[var(--gold)]/15 text-[var(--gold)]" : "bg-[hsl(35_22%_92%)] text-muted-foreground"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${s.is_active ? "bg-[var(--gold)]" : "bg-muted-foreground/60"}`} />
                          {s.status}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button data-testid={`report-btn-${s.id}`} onClick={() => openReport({ name: s.email, email: s.email, business: s.email })}
                            className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs hover:border-[var(--gold)] transition-colors whitespace-nowrap">
                            <FileBarChart size={13} /> Report
                          </button>
                          {s.status === "past_due" && (
                            <button data-testid={`remind-btn-${s.id}`} onClick={() => remindSub(s)}
                              className="inline-flex items-center gap-1.5 border border-[var(--gold)] px-3 py-1.5 text-xs text-[var(--gold)] hover:bg-[var(--gold)]/10 transition-colors whitespace-nowrap">
                              <BellRing size={13} /> Remind
                            </button>
                          )}
                          {s.is_active && (
                            <button data-testid={`cancel-btn-${s.id}`} onClick={() => cancelSub(s)}
                              className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-red-600 hover:border-red-400 transition-colors whitespace-nowrap">
                              <Ban size={13} /> Cancel
                            </button>
                          )}
                          {!s.is_active && (
                            <button data-testid={`winback-btn-${s.id}`} onClick={() => winbackSub(s)}
                              className="inline-flex items-center gap-1.5 bg-[var(--gold)] px-3 py-1.5 text-xs text-[var(--charcoal)] hover:bg-[var(--gold-light)] transition-colors whitespace-nowrap">
                              <Sparkle size={13} /> Welcome Back
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {subs.length === 0 && (<tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">No subscriptions yet — monthly clients will appear here.</td></tr>)}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Onboarding */}
          <TabsContent value="onboarding" className="mt-6">
            <div className="flex justify-end mb-4">
              <button data-testid="add-onboarding-btn" onClick={() => setObOpen(true)}
                className="inline-flex items-center gap-2 bg-secondary px-4 py-2.5 text-sm text-secondary-foreground hover:bg-[var(--gold)] transition-colors">
                <Plus size={14} /> Add client
              </button>
            </div>
            {onboarding.length === 0 ? (
              <div className="border border-border px-5 py-10 text-center text-muted-foreground">
                No onboarding checklists yet — add a client to track their setup.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {onboarding.map((o) => {
                  const done = ONBOARDING_STEPS.filter((st) => o.items?.[st]).length;
                  return (
                    <div key={o.client_email} data-testid={`onboarding-${o.client_email}`} className="border border-border p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="serif text-xl text-foreground">{o.client_name}</p>
                          <p className="text-xs text-muted-foreground">{o.client_email}</p>
                        </div>
                        <span className="serif text-lg text-[var(--gold)]">{done}/{ONBOARDING_STEPS.length}</span>
                      </div>
                      <div className="mt-4 h-1 bg-border">
                        <div className="h-1 bg-[var(--gold)] transition-all duration-500" style={{ width: `${(done / ONBOARDING_STEPS.length) * 100}%` }} />
                      </div>
                      <div className="mt-5 space-y-1">
                        {ONBOARDING_STEPS.map((st) => {
                          const on = !!o.items?.[st];
                          return (
                            <button key={st} data-testid={`step-${o.client_email}-${st}`} onClick={() => toggleStep(o, st)}
                              className="w-full flex items-center gap-3 text-left text-sm py-1.5 group">
                              <span className={`h-5 w-5 border flex items-center justify-center transition-colors ${on ? "bg-[var(--gold)] border-[var(--gold)]" : "border-border group-hover:border-[var(--gold)]"}`}>
                                {on && <Check size={13} className="text-[var(--charcoal)]" />}
                              </span>
                              <span className={on ? "text-muted-foreground line-through" : "text-foreground"}>{st}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Follow-Ups — band-based email sequences */}
          <TabsContent value="followups" className="mt-6">
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <p className="overline">Time-Wealth Audit Sequences</p>
              <p className="text-xs text-muted-foreground">
                Recovery = Critical Time Debt &amp; Operational Drag · Elevation = Leaking Hours &amp; Time-Wealthy
              </p>
            </div>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="followups-table">
                <thead className="bg-[hsl(35_22%_94%)] text-left">
                  <tr className="[&>th]:px-5 [&>th]:py-3 [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-xs [&>th]:text-muted-foreground">
                    <th>Prospect</th><th>Band</th><th>Sequence</th><th>Email</th><th>Send Date</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(followups || []).map((f) => (
                    <tr key={f.id} data-testid={`followup-row-${f.id}`} className="border-t border-border [&>td]:px-5 [&>td]:py-4 align-top">
                      <td className="whitespace-nowrap">
                        <div>{f.name}</div>
                        <div className="text-xs text-muted-foreground">{f.email}</div>
                      </td>
                      <td className="whitespace-nowrap">{f.band || "—"}</td>
                      <td className="whitespace-nowrap capitalize">{f.sequence}</td>
                      <td className="min-w-[220px]">{SEQUENCE_LABELS[f.sequence]?.[f.step] || `Step ${(f.step ?? 0) + 1}`}</td>
                      <td className="whitespace-nowrap text-muted-foreground">
                        {new Date(f.send_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </td>
                      <td className="whitespace-nowrap">
                        {f.sent ? (
                          <span data-testid={`followup-status-${f.id}`} className="px-2 py-0.5 text-xs bg-green-100 text-green-700 uppercase tracking-wider">
                            Sent
                          </span>
                        ) : f.cancelled ? (
                          <span data-testid={`followup-status-${f.id}`} className="px-2 py-0.5 text-xs bg-red-100 text-red-700 uppercase tracking-wider">
                            Cancelled
                          </span>
                        ) : f.paused ? (
                          <span data-testid={`followup-status-${f.id}`} className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 uppercase tracking-wider">
                            Paused
                          </span>
                        ) : f.send_failed ? (
                          <span data-testid={`followup-status-${f.id}`} className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 uppercase tracking-wider">
                            Retrying
                          </span>
                        ) : (
                          <span data-testid={`followup-status-${f.id}`} className="px-2 py-0.5 text-xs bg-[hsl(35_22%_92%)] text-muted-foreground uppercase tracking-wider">
                            Scheduled
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        {!f.sent && !f.cancelled && (
                          <div className="flex items-center gap-2">
                            {f.paused ? (
                              <button
                                data-testid={`followup-resume-${f.id}`}
                                onClick={() => controlFollowups(f.email, "resume")}
                                className="inline-flex items-center gap-1 border border-[var(--gold)] px-3 py-1.5 text-xs text-[var(--gold)] hover:bg-[var(--gold)]/10 transition-colors"
                              >
                                Resume
                              </button>
                            ) : (
                              <button
                                data-testid={`followup-pause-${f.id}`}
                                onClick={() => controlFollowups(f.email, "pause")}
                                className="inline-flex items-center gap-1 border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-[var(--gold)] hover:text-foreground transition-colors"
                              >
                                Pause
                              </button>
                            )}
                            <button
                              data-testid={`followup-cancel-${f.id}`}
                              onClick={() => controlFollowups(f.email, "cancel")}
                              className="inline-flex items-center gap-1 border border-red-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {followups && followups.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">
                      No follow-ups yet — they queue automatically when a prospect completes the Time-Wealth Audit.
                    </td></tr>
                  )}
                  {!followups && (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">Loading…</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Abandoned — started the audit but never finished */}
          <TabsContent value="abandoned" className="mt-6">
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <p className="overline">Abandoned Audits</p>
              <p className="text-xs text-muted-foreground">
                Warm prospects who started the Time-Wealth Audit and didn't finish — a personal note from you goes a long way
              </p>
            </div>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="abandoned-table">
                <thead className="bg-[hsl(35_22%_94%)] text-left">
                  <tr className="[&>th]:px-5 [&>th]:py-3 [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-xs [&>th]:text-muted-foreground">
                    <th>Prospect</th><th>Business</th><th>Started</th><th>Reminder Email</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(abandoned || []).map((a) => (
                    <tr key={a.email} data-testid={`abandoned-row-${a.email}`} className="border-t border-border [&>td]:px-5 [&>td]:py-4 align-top">
                      <td className="whitespace-nowrap">
                        <div>{a.name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{a.email}</div>
                      </td>
                      <td className="whitespace-nowrap">{a.business_name || "—"}</td>
                      <td className="whitespace-nowrap text-muted-foreground">
                        {a.started_at ? new Date(a.started_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </td>
                      <td className="whitespace-nowrap">
                        {a.nudged ? (
                          <span data-testid={`abandoned-nudge-${a.email}`} className="px-2 py-0.5 text-xs bg-green-100 text-green-700 uppercase tracking-wider">
                            Sent
                          </span>
                        ) : (
                          <span data-testid={`abandoned-nudge-${a.email}`} className="px-2 py-0.5 text-xs bg-[hsl(35_22%_92%)] text-muted-foreground uppercase tracking-wider">
                            Auto at 24h
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        <a
                          href={`mailto:${a.email}?subject=${encodeURIComponent("Your Time-Wealth Audit — a personal note")}&body=${encodeURIComponent(`Dear ${a.name || "there"},\n\nI noticed you began your Time-Wealth Audit and wanted to reach out personally. If any question gave you pause, I'd be glad to walk through it together — it only takes about three minutes.\n\nWarmly,\nXena Redd\nBeam Concierge`)}`}
                          data-testid={`abandoned-email-${a.email}`}
                          className="inline-flex items-center gap-1.5 border border-[var(--gold)] px-3 py-1.5 text-xs text-[var(--gold)] hover:bg-[var(--gold)]/10 transition-colors"
                        >
                          Email personally
                        </a>
                      </td>
                    </tr>
                  ))}
                  {abandoned && abandoned.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">
                      No abandoned audits — every prospect who started has finished.
                    </td></tr>
                  )}
                  {!abandoned && (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">Loading…</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Analytics — viewers, audits, calls, conversions */}
          <TabsContent value="analytics" className="mt-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-funnel">
              <StatCard testid="an-viewers" label="Site Viewers" value={analytics ? analytics.viewers : "—"} accent />
              <StatCard testid="an-audits" label="Audits Completed" value={analytics ? analytics.audits_completed : "—"} accent />
              <StatCard testid="an-calls" label="Operations Audit Calls" value={analytics ? analytics.audit_calls_viewed : "—"} />
              <StatCard testid="an-clients" label="Paying Clients" value={analytics ? analytics.clients : "—"} />
            </div>

            <div className="mt-6 border border-border" data-testid="analytics-conversions">
              <div className="px-6 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <p className="overline">Conversion Tracker</p>
                <p className="text-xs text-muted-foreground">How visitors become clients</p>
              </div>
              <div className="divide-y divide-border">
                {[
                  { label: "Advisory page viewers", value: analytics?.viewers_advisory, rate: null, testid: "conv-viewers" },
                  { label: "Viewers → Audits completed", value: analytics?.audits_completed, rate: analytics?.view_to_audit, testid: "conv-view-to-audit" },
                  { label: "Audits → Operations Audit Calls", value: analytics?.audit_calls_viewed, rate: analytics?.audit_to_call, testid: "conv-audit-to-call" },
                  { label: "All leads → Paying clients", value: analytics?.clients, rate: analytics?.lead_to_client, testid: "conv-lead-to-client" },
                ].map((row) => (
                  <div key={row.testid} data-testid={row.testid} className="px-6 py-4 flex items-center justify-between gap-4">
                    <span className="text-sm">{row.label}</span>
                    <span className="flex items-baseline gap-3">
                      <span className="serif text-2xl">{row.value ?? "—"}</span>
                      {row.rate !== null && row.rate !== undefined && (
                        <span className="text-xs uppercase tracking-wider text-[var(--gold)]">{row.rate}%</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              <div className="px-6 py-3 border-t border-border text-xs text-muted-foreground">
                Viewers split — home: {analytics?.viewers_home ?? 0} · advisory: {analytics?.viewers_advisory ?? 0} · audits started: {analytics?.audits_started ?? 0} ({analytics?.start_to_complete ?? 0}% finish)
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Invoice Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="invoice-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">Send an Invoice</DialogTitle>
            <DialogDescription>Generate a secure Stripe payment link and email it to your client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div><label className="overline block mb-1.5">Client Name</label>
              <input data-testid="invoice-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Client full name" /></div>
            <div><label className="overline block mb-1.5">Client Email</label>
              <input data-testid="invoice-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} placeholder="client@business.com" /></div>
            <div><label className="overline block mb-1.5">Pricing Option</label>
              <Select value={form.lookup_key} onValueChange={(v) => setForm((f) => ({ ...f, lookup_key: v }))}>
                <SelectTrigger data-testid="invoice-tier" className="w-full"><SelectValue placeholder="Choose a tier & charge" /></SelectTrigger>
                <SelectContent>
                  {tiers.map((t) => (<SelectItem key={t.lookup_key} value={t.lookup_key}>{t.label} — {t.display}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {resultUrl && (
              <div className="border border-border p-3 bg-[hsl(35_22%_96%)]">
                <p className="text-xs text-muted-foreground mb-2">{emailSent ? "Emailed to client. Payment link:" : "Payment link (copy & send manually):"}</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={resultUrl} className="flex-1 text-xs bg-transparent truncate outline-none" />
                  <button onClick={copyLink} data-testid="invoice-copy" className="shrink-0 p-1.5 border border-border hover:bg-background"><Copy size={14} /></button>
                </div>
              </div>
            )}
            <button data-testid="invoice-submit" onClick={submitInvoice} disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 bg-secondary px-6 py-3.5 text-sm text-secondary-foreground hover:bg-[var(--gold)] transition-colors disabled:opacity-60">
              {sending ? (<><Loader2 size={15} className="animate-spin" /> Creating…</>) : (<><Send size={15} /> Create &amp; Email Link</>)}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Monthly Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">Monthly Reputation Report</DialogTitle>
            <DialogDescription>Email your client a branded snapshot of their reputation growth.</DialogDescription>
          </DialogHeader>
          {report && (
            <div className="space-y-4 pt-2 max-h-[65vh] overflow-y-auto pr-1">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="overline block mb-1.5">Client Name</label>
                  <input data-testid="report-name" value={report.client_name} onChange={(e) => setReport((r) => ({ ...r, client_name: e.target.value }))} className={inputCls} /></div>
                <div><label className="overline block mb-1.5">Client Email</label>
                  <input data-testid="report-email" value={report.client_email} onChange={(e) => setReport((r) => ({ ...r, client_email: e.target.value }))} className={inputCls} /></div>
                <div><label className="overline block mb-1.5">Business Name</label>
                  <input data-testid="report-business" value={report.business_name} onChange={(e) => setReport((r) => ({ ...r, business_name: e.target.value }))} className={inputCls} /></div>
                <div><label className="overline block mb-1.5">Month</label>
                  <input data-testid="report-month" value={report.month} onChange={(e) => setReport((r) => ({ ...r, month: e.target.value }))} className={inputCls} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="overline block mb-1.5">Rating</label>
                  <input data-testid="report-rating" value={report.rating} onChange={(e) => setReport((r) => ({ ...r, rating: e.target.value }))} className={inputCls} placeholder="4.8" /></div>
                <div><label className="overline block mb-1.5">New Reviews</label>
                  <input value={report.new_reviews} onChange={(e) => setReport((r) => ({ ...r, new_reviews: e.target.value }))} className={inputCls} placeholder="27" /></div>
                <div><label className="overline block mb-1.5">Total Reviews</label>
                  <input value={report.total_reviews} onChange={(e) => setReport((r) => ({ ...r, total_reviews: e.target.value }))} className={inputCls} placeholder="312" /></div>
              </div>
              <div><label className="overline block mb-1.5">Search Ranking Note</label>
                <input value={report.ranking_note} onChange={(e) => setReport((r) => ({ ...r, ranking_note: e.target.value }))} className={inputCls} placeholder="Moved from #6 to #2 for 'best bistro charlotte'" /></div>
              <div><label className="overline block mb-1.5">Personal Note</label>
                <textarea value={report.notes} onChange={(e) => setReport((r) => ({ ...r, notes: e.target.value }))} rows={2} className={`${inputCls} resize-none`} placeholder="A warm note to your client…" /></div>
              {history.length > 0 && (
                <div className="border border-border" data-testid="report-history">
                  <p className="overline px-3 pt-3">Report History</p>
                  <div className="px-3 pb-3 pt-2 space-y-1.5 max-h-32 overflow-y-auto">
                    {history.map((h) => (
                      <div key={h.id} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{h.month}</span>
                        <span className="text-foreground">
                          {h.rating ? `★ ${h.rating}` : "—"}
                          {h.total_reviews ? ` · ${h.total_reviews} reviews` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {preview && (
                <div className="border border-border" data-testid="report-preview">
                  <div className="flex items-center justify-between px-3 py-2 bg-[hsl(35_22%_94%)] border-b border-border">
                    <span className="overline">Email Preview</span>
                    <button onClick={() => setPreview("")} className="text-xs text-muted-foreground hover:text-foreground">Close</button>
                  </div>
                  <iframe title="report-preview" srcDoc={preview} className="w-full h-72 bg-white" />
                </div>
              )}
              <div className="flex items-center gap-3">
                <button data-testid="report-preview-btn" onClick={previewReport} disabled={previewing}
                  className="inline-flex items-center justify-center gap-2 border border-border px-5 py-3.5 text-sm hover:border-[var(--gold)] transition-colors disabled:opacity-60">
                  {previewing ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Preview
                </button>
                <button data-testid="report-submit" onClick={submitReport} disabled={reportSending}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-secondary px-6 py-3.5 text-sm text-secondary-foreground hover:bg-[var(--gold)] transition-colors disabled:opacity-60">
                  {reportSending ? (<><Loader2 size={15} className="animate-spin" /> Sending…</>) : (<><FileBarChart size={15} /> Send Report</>)}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Onboarding Client Dialog */}
      <Dialog open={obOpen} onOpenChange={setObOpen}>
        <DialogContent data-testid="onboarding-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">New Onboarding Checklist</DialogTitle>
            <DialogDescription>Track a new client's setup so nothing slips.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <label className="overline block mb-1.5">Client Name</label>
              <input data-testid="onboarding-name" value={obForm.name}
                onChange={(e) => setObForm((f) => ({ ...f, name: e.target.value }))}
                className={inputCls} placeholder="Business or owner name" />
            </div>
            <div>
              <label className="overline block mb-1.5">Client Email</label>
              <input data-testid="onboarding-email" value={obForm.email}
                onChange={(e) => setObForm((f) => ({ ...f, email: e.target.value }))}
                className={inputCls} placeholder="client@business.com" />
            </div>
            <button data-testid="onboarding-submit" onClick={addOnboarding}
              className="w-full inline-flex items-center justify-center gap-2 bg-secondary px-6 py-3.5 text-sm text-secondary-foreground hover:bg-[var(--gold)] transition-colors">
              <Plus size={15} /> Create Checklist
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
